import "./style.scss";
import { FC, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/src/store";
import {
  setColumnWidth,
  setCurrentMainDocForUser,
  setRowFieldValue,
  toggleRowSelection,
  toggleAllRows,
} from "@/src/store/slices/AdmissionOfficeDatabase/AODatabaseTableSlice";
import { IRowData } from "@/src/types/applicationOnline";
import { IADatabaseDocument, IADatabaseFilters } from "@/src/types/admissionDatabase";
import ArrowFilter from "@/src/svg/applicationsPage/ArrowFilter";
import VerticalLine from "../../VerticalLine/VerticalLine";
import GuidelineSvg from "@/src/svg/sidebar/GuidelineSvg";
import AODFilterPopUp from "../AODFilterPopUp/AODFilterPopUp";
import {
  admissionDatabaseFormOfficeConst,
  admissionDatabaseIsAvailableConst,
  admissionDatabaseOfficeUrlConst,
  resolveAdmissionDatabaseFormOfficeId,
  resolveAdmissionDatabaseFormOfficeLabel,
  resolveAdmissionDatabaseIsAvailableId,
  resolveAdmissionDatabaseIsAvailableLabel,
  resolveAdmissionDatabaseOfficeUrlId,
  resolveAdmissionDatabaseOfficeUrlLabel,
} from "@/src/constants/AdmissionOffice/admissionOfficeDatabaseConsts";
import { admissionDatabaseRequests } from "@/src/api/admissionOfficeDatabaseRequests";
import { toOfficeUrlForApi } from "@/src/utils/accessControl/formatOfficeUrl";
import {
  executeCurrentMainDocumentAction,
  resolveCurrentMainDocumentButtonKind,
} from "@/src/utils/admissionOffice/executeCurrentMainDocumentAction";
import { usePushNotifications } from "@/src/components/CustomUtils/PushNotifications/PushNotificationsProvider";
import { setIsPreloyder } from "@/src/store/slices/Preloyder/PreloyderSlice";

const CURRENT_MAIN_REGISTER_CONFIRM = "Are you sure you want to create a record in Office Accounting?";
const CURRENT_MAIN_SWITCH_CONFIRM = "Are you sure you want to switch the main document in Office Accounting?";
const CURRENT_MAIN_ALREADY_MAIN_MESSAGE = "This is the current document in Office Accounting.";

type AodSelectColumnId = "is_available" | "office_url" | "form_office";

const SELECT_COLUMN_IDS: AodSelectColumnId[] = ["is_available", "office_url", "form_office"];

// vocabulary id → accessor for data
const columnAccessors: Record<string, (row: IADatabaseDocument) => React.ReactNode> = {
  data_added: (row) => row.data_added,
  full_name: (row) => row.full_name,
  title: (row) => row.title,
  document_type: (row) => row.document_type,
  document_seria: (row) => row.document_seria,
  date_of_birth: (row) => row.date_of_birth,
  nationality: (row) => row.nationality,
  course: (row) => row.course,
  whose_doc: (row) => row.whose_doc,
  office_url: (row) => resolveAdmissionDatabaseOfficeUrlLabel(row.office_url),
  form_office: (row) => resolveAdmissionDatabaseFormOfficeLabel(row.form_office),
};

const buildDocumentUpdatePayload = (
  row: IADatabaseDocument,
  patch: Partial<{ is_available: boolean; office_url: string | null; form_office: string | null }>,
) => ({
  is_avaliable: patch.is_available ?? row.is_available !== false,
  is_main: row.is_main === true,
  office_url:
    patch.office_url !== undefined ? toOfficeUrlForApi(patch.office_url) : toOfficeUrlForApi(row.office_url),
  form_office: patch.form_office !== undefined ? patch.form_office : row.form_office ?? null,
});

interface APBodyProps {
  headerCheckboxRef: React.RefObject<HTMLInputElement | null>;
  setIsApPopUpFullName: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentDataPopUp: React.Dispatch<React.SetStateAction<IRowData | undefined>>;
  setIsCurrentPage: React.Dispatch<React.SetStateAction<string>>;

  tableFiltersList: IADatabaseFilters | undefined;
  isShowSettingsModal: boolean;
}

const AODBody: FC<APBodyProps> = ({
  headerCheckboxRef,
  setIsCurrentPage,
  tableFiltersList,
  isShowSettingsModal,
}) => {
  const dispatch = useDispatch();
  const { notify } = usePushNotifications();
  const { data, visibleColumns, selectedRows } = useSelector((state: RootState) => state.AODatabaseTable);
  const [filterPopUp, setFilterPopUp] = useState<string | null>(null);
  const [openTableParamMenu, setOpenTableParamMenu] = useState<string>("");
  const [openTableParamMenuRowId, setOpenTableParamMenuRowId] = useState<number | null>(null);
  const allChecked = selectedRows.length === data.length && data.length > 0;
  const someChecked = selectedRows.length > 0 && selectedRows.length < data.length;

  const [fixedColumnIndex, setFixedColumnIndex] = useState<number | null>(null);
  const [isAttach, setIsAttach] = useState<number | null>(null);

  const handleAttach = (index: number) => {
    if (isShowSettingsModal) {
      setFixedColumnIndex((prev) => (prev === index ? null : index));
      setIsAttach(index);
      if (isAttach === index) {
        setIsAttach(null);
      }
    }
  };

  const CHECKER_WIDTH = 4.12;

  const getStickyStyles = (index: number, section: "thead" | "tbody") => {
    if (fixedColumnIndex === null || index > fixedColumnIndex) return {};
    const left = CHECKER_WIDTH + visibleColumns.slice(0, index).reduce((acc, col) => acc + (col.width || 6), 0);
    return {
      position: "sticky" as const,
      left: `${left}rem`,
      zIndex: section === "thead" ? 7 : 5,
    };
  };

  const dragData = useRef<{ startX: number; thId: string | null; startWidth: number }>({
    startX: 0,
    thId: null,
    startWidth: 0,
  });

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>, thId: string, startWidth: number) => {
    if (!isShowSettingsModal) return;
    e.stopPropagation();
    e.preventDefault();
    dragData.current = { startX: e.clientX, thId, startWidth };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const onMouseMove = (e: MouseEvent) => {
    if (!isShowSettingsModal || !dragData.current.thId) return;
    const deltaRem = (e.clientX - dragData.current.startX) / 16;
    let newWidth = dragData.current.startWidth + deltaRem;
    if (newWidth < 2) newWidth = 2;
    dispatch(setColumnWidth({ id: dragData.current.thId, width: newWidth }));
  };

  const onMouseUp = () => {
    if (!isShowSettingsModal) return;
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", onMouseUp);
    dragData.current = { startX: 0, thId: null, startWidth: 0 };
  };

  // ====== drag-to-scroll (horizontal, thead only) ======
  const scrollRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const scrollStartX = useRef(0);
  const scrollStartScrollLeft = useRef(0);

  const isTheadScrollTarget = (target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) return false;
    return (
      !target.closest("input") &&
      !target.closest(".custom-checkbox") &&
      !target.closest(".cell-wrapper-arrow") &&
      !target.closest(".cell-wrapper-attach") &&
      !target.closest(".vertical-line") &&
      !target.closest(".admission-database-filter")
    );
  };

  const onTheadScrollMouseDown = (e: React.MouseEvent) => {
    if (isShowSettingsModal || !scrollRef.current || !isTheadScrollTarget(e.target)) return;
    isDragging.current = true;
    scrollStartX.current = e.clientX;
    scrollStartScrollLeft.current = scrollRef.current.scrollLeft;
  };

  const onTheadScrollMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current || !scrollRef.current) return;
    e.preventDefault();
    const dx = e.clientX - scrollStartX.current;
    scrollRef.current.scrollLeft = scrollStartScrollLeft.current - dx;
  };

  const stopTheadScrollDrag = () => {
    isDragging.current = false;
  };

  const onTheadScrollTouchStart = (e: React.TouchEvent) => {
    if (isShowSettingsModal || !scrollRef.current || !isTheadScrollTarget(e.target)) return;
    isDragging.current = true;
    scrollStartX.current = e.touches[0].clientX;
    scrollStartScrollLeft.current = scrollRef.current.scrollLeft;
  };

  const onTheadScrollTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current || !scrollRef.current) return;
    const dx = e.touches[0].clientX - scrollStartX.current;
    scrollRef.current.scrollLeft = scrollStartScrollLeft.current - dx;
  };

  const replacingBackendValueToCustom: Record<string, string> = {
    APPLY_ONLINE: "Apply Online",
    NEW: "New",
  };

  const toggleTableParamMenu = (menuType: string, rowId: number) => {
    if (openTableParamMenu === menuType && openTableParamMenuRowId === rowId) {
      setOpenTableParamMenu("");
      setOpenTableParamMenuRowId(null);
    } else {
      setOpenTableParamMenu(menuType);
      setOpenTableParamMenuRowId(rowId);
    }
  };

  const closeTableParamMenu = () => {
    setOpenTableParamMenu("");
    setOpenTableParamMenuRowId(null);
  };

  const handleDocumentFieldChange = async (
    event: React.MouseEvent<HTMLDivElement>,
    rowId: number,
    field: AodSelectColumnId,
    nextValue: boolean | string | null,
  ) => {
    event.stopPropagation();
    closeTableParamMenu();

    const targetRow = data.find((item) => item.id === rowId);
    if (!targetRow) return;

    const currentValue =
      field === "is_available"
        ? targetRow.is_available !== false
        : field === "office_url"
          ? targetRow.office_url ?? null
          : targetRow.form_office ?? null;

    if (currentValue === nextValue) return;

    dispatch(setRowFieldValue({ id: rowId, field, value: nextValue }));

    const payload = buildDocumentUpdatePayload(targetRow, {
      ...(field === "is_available" ? { is_available: Boolean(nextValue) } : {}),
      ...(field === "office_url" ? { office_url: nextValue as string | null } : {}),
      ...(field === "form_office" ? { form_office: nextValue as string | null } : {}),
    });

    const response = await admissionDatabaseRequests.updateDocument(rowId, payload);
    if (!response) {
      dispatch(setRowFieldValue({ id: rowId, field, value: currentValue }));
    }
  };

  const handleCurrentMainDocumentClick = async (
    event: React.MouseEvent<HTMLDivElement>,
    targetRow: IADatabaseDocument,
  ) => {
    event.stopPropagation();

    const buttonKind = resolveCurrentMainDocumentButtonKind(targetRow);
    if (!buttonKind) return;

    if (buttonKind === "main") {
      window.alert(CURRENT_MAIN_ALREADY_MAIN_MESSAGE);
      return;
    }

    const confirmMessage =
      buttonKind === "registration" ? CURRENT_MAIN_REGISTER_CONFIRM : CURRENT_MAIN_SWITCH_CONFIRM;
    if (!window.confirm(confirmMessage)) return;

    dispatch(setIsPreloyder(true));
    try {
      const result = await executeCurrentMainDocumentAction(
        targetRow,
        buttonKind === "registration" ? "register" : "switch",
      );
      if (!result.ok) {
        notify({
          title: buttonKind === "registration" ? "Office Accounting failed" : "Switch failed",
          message: result.message,
          type: "error",
        });
        return;
      }

      if (result.userId != null && result.currentMainDoc) {
        dispatch(
          setCurrentMainDocForUser({
            userId: result.userId,
            currentMainDoc: result.currentMainDoc,
          }),
        );
      }

      notify({
        title: buttonKind === "registration" ? "Registration Doc completed" : "Switched to main document",
        message: result.message,
        type: "success",
      });
    } catch (error) {
      console.error("current_main_doc action error:", error);
      notify({
        title: "Current Document failed",
        message: "Unexpected error while updating Current Document.",
        type: "error",
      });
    } finally {
      dispatch(setIsPreloyder(false));
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        !target.closest(".status-menu") &&
        !target.closest(".is-available-option") &&
        !target.closest(".aod-select-option")
      ) {
        closeTableParamMenu();
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const isSelectColumn = (id: string): id is AodSelectColumnId =>
    SELECT_COLUMN_IDS.includes(id as AodSelectColumnId);

  return (
    <div
      className={`admission-database-body ${isShowSettingsModal ? "settings-mode" : ""}`}
      ref={scrollRef}
      onMouseMove={onTheadScrollMouseMove}
      onMouseUp={stopTheadScrollDrag}
      onMouseLeave={stopTheadScrollDrag}
      onTouchMove={onTheadScrollTouchMove}
      onTouchEnd={stopTheadScrollDrag}
      onTouchCancel={stopTheadScrollDrag}
    >
      <table>
        <thead onMouseDown={onTheadScrollMouseDown} onTouchStart={onTheadScrollTouchStart}>
          <tr>
            <th id="table-checker" style={{ width: `${CHECKER_WIDTH}rem` }}>
              <div className="cell-wrapper">
                <label className={`custom-checkbox ${someChecked && !allChecked ? "indeterminate" : ""}`}>
                  <input
                    type="checkbox"
                    checked={allChecked}
                    ref={headerCheckboxRef}
                    onChange={() => dispatch(toggleAllRows())}
                  />
                  <span className="checkmark"></span>
                </label>
                <div className="vertical-line">
                  <VerticalLine />
                </div>
              </div>
            </th>
            {visibleColumns.map(({ title, id, width }, index) => (
              <th
                key={id}
                className={isSelectColumn(id) || id === "current_main_doc" ? id : undefined}
                style={{
                  width: `${width}rem`,
                  minWidth: `${width}rem`,
                  maxWidth: `${width}rem`,
                  ...getStickyStyles(index, "thead"),
                }}
              >
                <div className="cell-wrapper">
                  <p>{title}</p>
                  <div className="cell-wrapper-actions">
                    {(isShowSettingsModal || isAttach === index) && (
                      <div className="cell-wrapper-attach" onClick={() => handleAttach(index)}>
                        <GuidelineSvg />
                      </div>
                    )}
                    {(id === "data_added" ||
                      id === "title" ||
                      id === "document_type" ||
                      id === "nationality" ||
                      id === "course" ||
                      id === "whose_doc") && (
                      <div
                        className={`cell-wrapper-arrow ${filterPopUp === id ? "rotate" : ""}`}
                        onClick={() => setFilterPopUp(filterPopUp === id ? null : id)}
                      >
                        <ArrowFilter />
                      </div>
                    )}
                  </div>
                  <div
                    className="vertical-line"
                    onMouseDown={isShowSettingsModal ? (e) => onMouseDown(e, id, width || 6) : undefined}
                    style={{ cursor: isShowSettingsModal ? "ew-resize" : undefined }}
                  >
                    <VerticalLine />
                  </div>
                  <AODFilterPopUp
                    id={id}
                    title={title}
                    tableFiltersList={tableFiltersList}
                    filterPopUp={filterPopUp}
                    setFilterPopUp={setFilterPopUp}
                    setIsCurrentPage={setIsCurrentPage}
                  />
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr key={row.id} className={row.id != null && selectedRows.includes(row.id) ? "table-row-selected" : undefined}>
              <td id="table-checker" style={{ width: `${CHECKER_WIDTH}rem` }}>
                <div className="cell-wrapper">
                  <label className="custom-checkbox">
                    <input
                      type="checkbox"
                      checked={row.id != null && selectedRows.includes(row.id)}
                      onChange={() => {
                        if (row.id != null) dispatch(toggleRowSelection(row.id));
                      }}
                    />
                    <span className="checkmark"></span>
                  </label>
                  <div className="vertical-line">
                    <VerticalLine />
                  </div>
                </div>
              </td>
              {visibleColumns.map(({ id, width }, index) => {
                const rawValue = String(columnAccessors[id]?.(row) ?? "");
                const displayValue = replacingBackendValueToCustom[rawValue] ?? rawValue;
                const isAvailableStatusId = resolveAdmissionDatabaseIsAvailableId(row.is_available);
                const isAvailableStatusLabel = resolveAdmissionDatabaseIsAvailableLabel(row.is_available);
                const officeUrlId = resolveAdmissionDatabaseOfficeUrlId(row.office_url);
                const officeUrlLabel = resolveAdmissionDatabaseOfficeUrlLabel(row.office_url);
                const formOfficeId = resolveAdmissionDatabaseFormOfficeId(row.form_office);
                const formOfficeLabel = resolveAdmissionDatabaseFormOfficeLabel(row.form_office);
                const currentMainButtonKind = resolveCurrentMainDocumentButtonKind(row);
                const currentMainButtonLabel =
                  currentMainButtonKind === "registration"
                    ? "Registration Doc"
                    : currentMainButtonKind === "switch"
                      ? "Switch on Main"
                      : currentMainButtonKind === "main"
                        ? "Main Document"
                        : "";

                return (
                  <td
                    key={id}
                    className={isSelectColumn(id) || id === "current_main_doc" ? id : undefined}
                    style={{
                      width: `${width}rem`,
                      minWidth: `${width}rem`,
                      maxWidth: `${width}rem`,
                      ...getStickyStyles(index, "tbody"),
                    }}
                  >
                    <div
                      className={`cell-wrapper ${id === "full_name" ? "clicable" : ""} ${
                        id === "is_available"
                          ? "is-available-cell clicable"
                          : id === "office_url" || id === "form_office"
                            ? "aod-select-cell clicable"
                            : id === "current_main_doc" && currentMainButtonKind
                              ? "current-main-doc-cell clicable"
                              : ""
                      }`}
                      {...(isSelectColumn(id) && row.id != null
                        ? {
                            onClick: (e: React.MouseEvent) => {
                              e.stopPropagation();
                              toggleTableParamMenu(id, row.id!);
                            },
                          }
                        : {})}
                    >
                      {id === "is_available" ? (
                        <>
                          <div className={`status-option is-available-option ${isAvailableStatusId}`}>
                            <span className="status-label">{isAvailableStatusLabel}</span>
                          </div>
                          {openTableParamMenu === "is_available" && openTableParamMenuRowId === row.id && (
                            <div className="status-menu">
                              {admissionDatabaseIsAvailableConst.map(({ id: optionId, label }) => (
                                <div
                                  key={optionId}
                                  className={`status-option is-available-option ${optionId}`}
                                  onClick={(e) => {
                                    if (row.id != null) {
                                      handleDocumentFieldChange(e, row.id, "is_available", optionId === "true");
                                    }
                                  }}
                                >
                                  <span className="status-label">{label}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      ) : id === "current_main_doc" ? (
                        currentMainButtonKind ? (
                          <div
                            className={`status-option current-main-doc-option ${currentMainButtonKind}`}
                            onClick={(e) => handleCurrentMainDocumentClick(e, row)}
                          >
                            <span className="status-label">{currentMainButtonLabel}</span>
                          </div>
                        ) : (
                          <div className="cell-content" />
                        )
                      ) : id === "office_url" ? (
                        <>
                          <div className="status-option aod-select-option">
                            <span className="status-label">{officeUrlLabel}</span>
                          </div>
                          {openTableParamMenu === "office_url" && openTableParamMenuRowId === row.id && (
                            <div className="status-menu">
                              {admissionDatabaseOfficeUrlConst.map(({ id: optionId, label, value }) => (
                                <div
                                  key={optionId}
                                  className={`status-option aod-select-option ${officeUrlId === optionId ? "selected" : ""}`}
                                  onClick={(e) => {
                                    if (row.id != null) {
                                      handleDocumentFieldChange(e, row.id, "office_url", value);
                                    }
                                  }}
                                >
                                  <span className="status-label">{label}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      ) : id === "form_office" ? (
                        <>
                          <div className="status-option aod-select-option">
                            <span className="status-label">{formOfficeLabel}</span>
                          </div>
                          {openTableParamMenu === "form_office" && openTableParamMenuRowId === row.id && (
                            <div className="status-menu">
                              {admissionDatabaseFormOfficeConst.map(({ id: optionId, label, value }) => (
                                <div
                                  key={optionId}
                                  className={`status-option aod-select-option ${formOfficeId === optionId ? "selected" : ""}`}
                                  onClick={(e) => {
                                    if (row.id != null) {
                                      handleDocumentFieldChange(e, row.id, "form_office", value);
                                    }
                                  }}
                                >
                                  <span className="status-label">{label}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="cell-content">{displayValue}</div>
                      )}
                      <div className="vertical-line">
                        <VerticalLine />
                      </div>
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AODBody;
